import { useState, useEffect, useMemo } from 'react';
import { StreamingMetadata, ViewerError } from '../types/viewer.types';
import { useStreamingMetadata } from './useStreamingMetadata';
import { contentService } from '../services';

/**
 * Hook for getting direct content URL - either presigned URL or streaming URL
 * Prefers presigned URLs when available for better performance
 */
export interface UseDirectContent {
  contentUrl: string | null;
  metadata: StreamingMetadata | null;
  loading: boolean;
  error: ViewerError | null;
  isDirectDownload: boolean;
  downloadMethod: 'single' | 'chunked';
}

export const useDirectContent = (contentId: string, teamId?: string, initialMetadata?: StreamingMetadata): UseDirectContent => {
  const { metadata, loading: metadataLoading, error: metadataError } = useStreamingMetadata(contentId, teamId, initialMetadata);
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [isDirectDownload, setIsDirectDownload] = useState<boolean>(false);

  useEffect(() => {
    if (!metadata) {
      setContentUrl(null);
      setIsDirectDownload(false);
      return;
    }

    // Check if we have a presigned URL (for public links or direct downloads)
    if (metadata.presignedUrl) {
      console.log('Using presigned URL for content:', contentId, '(strategy:', metadata.downloadStrategy, ')');
      setContentUrl(metadata.presignedUrl);
      setIsDirectDownload(metadata.downloadStrategy === 'direct');
    } else {
      // Fallback to streaming URL
      console.log('Using streaming URL for content:', contentId);
      const streamUrl = contentService.getContentStreamingUrl(contentId);
      setContentUrl(streamUrl);
      setIsDirectDownload(false);
    }
  }, [metadata, contentId]);

  const downloadMethod = useMemo(() => {
    return metadata?.downloadMethod || 'single';
  }, [metadata]);

  return {
    contentUrl,
    metadata,
    loading: metadataLoading,
    error: metadataError,
    isDirectDownload,
    downloadMethod
  };
};